/**
 * Home screen — gauge + quick actions + stats + key terms + tools grid.
 *
 * No new server data is invented. Every widget either:
 *   - derives from existing endpoints we already have, or
 *   - hides itself when the data isn't available.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Sparkles,
  ClipboardCheck,
  AlertTriangle,
  RefreshCw,
  Loader2,
  ChevronLeft,
  FileText,
} from 'lucide-react';
import type { TabKey } from '../shell/AppShell';
import { resolveContractContext } from '../utils/contractResolver';
import { getDocumentComments, getDocumentText } from '../utils/wordApi';
import { analyzeContractRisks, type RiskItem } from '../utils/openai';
import { Chatbot } from '../components/Chatbot';
import { Playbook } from '../components/Playbook';
import { RiskAnalysis } from '../components/RiskAnalysis';
import { ContractSummary } from '../components/ContractSummary';

type HomeFeature = 'assistant' | 'review' | 'risk' | 'summary' | null;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

interface HomeScreenProps {
  onNavigate: (tab: TabKey, view?: string) => void;
  /** Notifies the shell when the full-height chat assistant is open/closed so
   *  it can switch to the flush (full-height, no-padding) layout. */
  onAssistantOpenChange?: (open: boolean) => void;
}

interface Stats {
  openRedlines: number | null;
  playbookGaps: number | null;
  comments: number | null;
}

interface KeyTerms {
  contract_type_name: string | null;
  counter_party: string | null;
  amount: number | null;
  start_date: string | null;
  end_date: string | null;
  executed_date: string | null;
  status: string | null;
}

function authHeaders() {
  return {
    'x-addin-token': API_TOKEN,
    'ngrok-skip-browser-warning': 'true',
  } as Record<string, string>;
}

function risksCacheKey(ctx: { contractId: string | null; tenantId: string } | null) {
  if (!ctx) return null;
  return `ezylex:risks:${ctx.contractId || ctx.tenantId}`;
}

function autoRanKey(ctx: { contractId: string | null; tenantId: string } | null) {
  if (!ctx) return null;
  return `ezylex:auto-ran:${ctx.contractId || ctx.tenantId}`;
}

export function HomeScreen({ onAssistantOpenChange }: HomeScreenProps) {
  const [stats, setStats] = useState<Stats>({
    openRedlines: null,
    playbookGaps: null,
    comments: null,
  });
  const [keyTerms, setKeyTerms] = useState<KeyTerms | null>(null);
  const [risks, setRisks] = useState<RiskItem[] | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [autoStatus, setAutoStatus] = useState<{
    // 'none' = no playbook is configured for this contract (distinct from
    // 'done' which means a playbook actually ran).
    playbook: 'idle' | 'running' | 'done' | 'skipped' | 'none';
    risks: 'idle' | 'running' | 'done' | 'skipped';
  }>({ playbook: 'idle', risks: 'idle' });
  // Whether a playbook is configured for this contract (independent of whether
  // the auto-run ran). false → show "no playbook" instead of "done"/"0 issues".
  const [hasPlaybook, setHasPlaybook] = useState<boolean | null>(null);
  const [homeFeature, setHomeFeature] = useState<HomeFeature>(null);
  const ctxRef = useRef<{ contractId: string | null; tenantId: string } | null>(null);
  const autoTriggeredRef = useRef<boolean>(false);

  // Tell the shell to use the flush (full-height) layout while the chat is open.
  useEffect(() => {
    onAssistantOpenChange?.(homeFeature === 'assistant');
    return () => onAssistantOpenChange?.(false);
  }, [homeFeature, onAssistantOpenChange]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const ctx = await resolveContractContext();
      if (!ctx?.tenantId || cancelled) return;
      ctxRef.current = { contractId: ctx.contractId || null, tenantId: ctx.tenantId };

      // If this contract was already auto-analyzed earlier in this session,
      // jump straight to a "settled" state so the gauge doesn't show the
      // loader on a tab-switch back to Home. The auto-run gate will keep
      // these set to 'skipped' once it runs.
      try {
        const flag = autoRanKey(ctxRef.current);
        if (flag && sessionStorage.getItem(flag) === '1' && !cancelled) {
          setAutoStatus({ playbook: 'skipped', risks: 'skipped' });
        }
      } catch {}

      // Hydrate AI risks from this session's cache so the gauge isn't blind
      // to results that were generated on the Risk tab earlier.
      try {
        const key = risksCacheKey(ctxRef.current);
        if (key) {
          const raw = sessionStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed?.risks) && !cancelled) {
              setRisks(parsed.risks as RiskItem[]);
            }
          }
        }
      } catch {}

      // Pending playbook run gives us redline + gap counts.
      // Prefer the contract-scoped endpoint so Home matches the Review tab
      // exactly; fall back to the tenant-scoped lookup only when contractId
      // isn't resolved (e.g. third-party docx).
      try {
        const url = ctx.contractId
          ? `${API_BASE_URL}/api/contracts/${ctx.contractId}/pending-redlines`
          : `${API_BASE_URL}/api/addin/latest-pending-playbook-run?tenantId=${encodeURIComponent(ctx.tenantId)}`;
        const res = await fetch(url, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          const pending = Array.isArray(data?.pending) ? data.pending : [];
          const openCount = pending.filter((p: any) => !p.redline_applied).length;
          if (!cancelled) {
            setStats((s) => ({
              ...s,
              openRedlines: openCount,
              playbookGaps: pending.length,
            }));
            window.dispatchEvent(
              new CustomEvent('ezylex:redlines-updated', {
                detail: { openRedlines: openCount },
              })
            );
          }
        } else if (res.status === 404 || res.status === 204) {
          // No run for this contract yet — zero out so we don't leak counts
          // from a previous contract's tenant-scoped fallback.
          if (!cancelled) {
            setStats((s) => ({ ...s, openRedlines: 0, playbookGaps: 0 }));
            window.dispatchEvent(
              new CustomEvent('ezylex:redlines-updated', {
                detail: { openRedlines: 0 },
              })
            );
          }
        }
      } catch {}

      // Is a playbook configured for this contract? Checked independently of the
      // auto-run (which is skipped when redlines already exist), so the card can
      // say "no playbook" instead of "done" / "0 playbook issues".
      if (ctx.contractId) {
        try {
          const res = await fetch(
            `${API_BASE_URL}/api/contracts/${ctx.contractId}/playbook`,
            { headers: authHeaders() }
          );
          if (res.ok && !cancelled) {
            const data = await res.json();
            const pbs = (data?.playbooks ?? []) as Array<{ id: string }>;
            setHasPlaybook(pbs.length > 0);
          }
        } catch {
          /* leave as unknown */
        }
      }

      // Contract metadata (key terms — only fields we actually have)
      if (ctx.contractId) {
        try {
          const res = await fetch(
            `${API_BASE_URL}/api/addin/contract-key-terms?tenantId=${encodeURIComponent(ctx.tenantId)}&contractId=${encodeURIComponent(ctx.contractId)}`,
            { headers: authHeaders() }
          );
          if (res.ok) {
            const data = await res.json();
            if (!cancelled && data?.contract) {
              setKeyTerms(data.contract);
            }
          }
        } catch {}
      }

      // Word doc comments
      try {
        const comments = await getDocumentComments().catch(() => []);
        if (!cancelled) setStats((s) => ({ ...s, comments: comments.length }));
      } catch {
        if (!cancelled) setStats((s) => ({ ...s, comments: 0 }));
      }

      // Auto-run analysis once per (contract, session). Skip if a recent
      // run is already in cache. This is the user's expectation: opening
      // the dashboard should give a real score, not "tap to analyze".
      if (cancelled || autoTriggeredRef.current) return;
      autoTriggeredRef.current = true;

      const sessionKey = autoRanKey(ctxRef.current);
      const alreadyRanThisSession =
        !!sessionKey && sessionStorage.getItem(sessionKey) === '1';

      void autoRunIfNeeded(alreadyRanThisSession);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoRunIfNeeded = async (alreadyRanThisSession: boolean) => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    // ---- Playbook auto-run ----
    // Only if: no existing pending redlines for this contract AND we haven't
    // already run during this session AND we have a contractId.
    const needPlaybook =
      !alreadyRanThisSession &&
      ctx.contractId &&
      (stats.playbookGaps ?? 0) === 0 &&
      (stats.openRedlines ?? 0) === 0;

    if (needPlaybook && ctx.contractId) {
      setAutoStatus((s) => ({ ...s, playbook: 'running' }));
      try {
        // Find a playbook bound to this contract type.
        const pbRes = await fetch(
          `${API_BASE_URL}/api/contracts/${ctx.contractId}/playbook`,
          { headers: authHeaders() }
        );
        const pbData = pbRes.ok ? await pbRes.json() : null;
        const playbooks = (pbData?.playbooks ?? []) as Array<{ id: string }>;
        if (playbooks.length === 0) {
          // No playbook bound to this contract type — report it as such rather
          // than pretending the playbook step "ran".
          setAutoStatus((s) => ({ ...s, playbook: 'none' }));
        } else {
          const text = await getDocumentText().catch(() => '');
          if (text && text.trim().length >= 20) {
            const runRes = await fetch(
              `${API_BASE_URL}/api/playbooks/${playbooks[0].id}/run?tenantId=${encodeURIComponent(ctx.tenantId)}`,
              {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  contract_id: ctx.contractId,
                  contract_text: text,
                  tenantId: ctx.tenantId,
                }),
              }
            );
            if (runRes.ok) {
              // Refetch pending redlines so the gauge reflects this run.
              const pendingRes = await fetch(
                `${API_BASE_URL}/api/contracts/${ctx.contractId}/pending-redlines`,
                { headers: authHeaders() }
              );
              if (pendingRes.ok) {
                const data = await pendingRes.json();
                const pending = Array.isArray(data?.pending) ? data.pending : [];
                const openCount = pending.filter((p: any) => !p.redline_applied).length;
                setStats((s) => ({
                  ...s,
                  openRedlines: openCount,
                  playbookGaps: pending.length,
                }));
                // Tell App.tsx so the bottom-nav badge updates without a reload.
                window.dispatchEvent(
                  new CustomEvent('ezylex:redlines-updated', {
                    detail: { openRedlines: openCount },
                  })
                );
              }
            }
          }
          setAutoStatus((s) => ({ ...s, playbook: 'done' }));
        }
      } catch {
        setAutoStatus((s) => ({ ...s, playbook: 'skipped' }));
      }
    } else {
      setAutoStatus((s) => ({ ...s, playbook: 'skipped' }));
    }

    // ---- Risk analysis auto-run ----
    // Only if: no cached risks AND we haven't already run during this session.
    const cachedRisksKey = risksCacheKey(ctx);
    const haveCachedRisks =
      !!cachedRisksKey && !!sessionStorage.getItem(cachedRisksKey);
    if (!alreadyRanThisSession && !haveCachedRisks) {
      setAutoStatus((s) => ({ ...s, risks: 'running' }));
      try {
        const text = await getDocumentText().catch(() => '');
        if (text && text.trim().length > 50) {
          const result = await analyzeContractRisks(text, ctx.tenantId);
          setRisks(result.risks);
          if (cachedRisksKey) {
            sessionStorage.setItem(
              cachedRisksKey,
              JSON.stringify({ risks: result.risks, ts: Date.now() })
            );
          }
          window.dispatchEvent(
            new CustomEvent('ezylex:risks-updated', { detail: { risks: result.risks } })
          );
        }
        setAutoStatus((s) => ({ ...s, risks: 'done' }));
      } catch {
        setAutoStatus((s) => ({ ...s, risks: 'skipped' }));
      }
    } else {
      setAutoStatus((s) => ({ ...s, risks: 'skipped' }));
    }

    // Mark this contract as auto-run for the session so a tab switch back
    // to Home doesn't trigger another full analysis.
    const flagKey = autoRanKey(ctx);
    if (flagKey) {
      try {
        sessionStorage.setItem(flagKey, '1');
      } catch {}
    }
  };

  // Pick up risk-analysis results when the Risk tab completes a run.
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (Array.isArray(detail?.risks)) setRisks(detail.risks as RiskItem[]);
    };
    window.addEventListener('ezylex:risks-updated', onUpdate as EventListener);
    return () => window.removeEventListener('ezylex:risks-updated', onUpdate as EventListener);
  }, []);

  // Pick up open-redline count changes (e.g. the user applied a redline on the
  // Redlining pane) so the dashboard gauge/score updates the moment they come
  // back to Home — without this the score was frozen because playbookGaps was
  // only ever set on the initial load / auto-run.
  useEffect(() => {
    const onRedlines = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.openRedlines === 'number') {
        setStats((s) => ({
          ...s,
          openRedlines: detail.openRedlines,
          playbookGaps: detail.openRedlines,
        }));
      }
    };
    window.addEventListener('ezylex:redlines-updated', onRedlines as EventListener);
    return () => window.removeEventListener('ezylex:redlines-updated', onRedlines as EventListener);
  }, []);

  const severityCounts = (() => {
    if (!risks) return { high: 0, medium: 0, low: 0 };
    return {
      high: risks.filter((r) => r.severity === 'high').length,
      medium: risks.filter((r) => r.severity === 'medium').length,
      low: risks.filter((r) => r.severity === 'low').length,
    };
  })();

  // Composite risk score: playbook gaps (heavy) + AI-identified risks weighted
  // by severity.
  //
  // Score is null until BOTH analyses have settled (done OR skipped). Showing
  // a playbook-only score while the AI analysis is still running misleads the
  // user — they'll see the gauge jump from 55 → 100 as risks land. Keep it
  // null and show the loader instead.
  const playbookSettled =
    autoStatus.playbook === 'done' ||
    autoStatus.playbook === 'skipped' ||
    autoStatus.playbook === 'none';
  // True when we know this contract has no playbook configured — via the
  // independent check (works even if the auto-run was skipped) or the auto-run.
  const noPlaybook = hasPlaybook === false || autoStatus.playbook === 'none';
  const risksSettled =
    autoStatus.risks === 'done' || autoStatus.risks === 'skipped';
  const bothSettled = playbookSettled && risksSettled;

  const riskScore: number | null = (() => {
    if (!bothSettled) return null;

    const playbookPoints = (stats.playbookGaps ?? 0) * 20;
    const riskPoints =
      severityCounts.high * 15 +
      severityCounts.medium * 8 +
      severityCounts.low * 3;

    const total = playbookPoints + riskPoints;
    if (total === 0) return 8; // both sides confirmed zero → healthy
    return Math.min(100, 15 + total);
  })();

  const handleRerunAnalysis = async () => {
    // Re-run in place on Home — dispatch the shared event and run our own
    // analysis so the gauge updates. (Previously this navigated to a 'risk'
    // tab that no longer exists, which fell through to the Clauses screen.)
    window.dispatchEvent(new CustomEvent('ezylex:analyze-all'));

    // Kick off our own background analysis so the gauge updates.
    const ctx = ctxRef.current;
    if (!ctx?.tenantId) return;
    setAnalyzing(true);
    try {
      const text = await getDocumentText().catch(() => '');
      if (text && text.trim().length > 50) {
        const result = await analyzeContractRisks(text, ctx.tenantId);
        setRisks(result.risks);
        const key = risksCacheKey(ctx);
        if (key) {
          try {
            sessionStorage.setItem(
              key,
              JSON.stringify({ risks: result.risks, ts: Date.now() })
            );
          } catch {}
        }
        window.dispatchEvent(
          new CustomEvent('ezylex:risks-updated', { detail: { risks: result.risks } })
        );
      }
    } catch (err) {
      console.warn('[Home] background risk analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const hasKeyTerms =
    !!keyTerms &&
    (keyTerms.contract_type_name ||
      keyTerms.counter_party ||
      keyTerms.amount ||
      keyTerms.start_date ||
      keyTerms.end_date ||
      keyTerms.executed_date ||
      keyTerms.status);

  // The chat assistant renders full-height and borderless (fills the flush shell).
  if (homeFeature === 'assistant') {
    return (
      <div className="flex-1 min-h-0 flex flex-col animate-in fade-in duration-150">
        <button
          type="button"
          onClick={() => setHomeFeature(null)}
          className="flex-shrink-0 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[var(--brand-3)] hover:underline px-4 pt-3 pb-2"
        >
          <ChevronLeft size={14} /> Back to Home
        </button>
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <Chatbot />
        </div>
      </div>
    );
  }

  if (homeFeature) {
    return (
      <div className="space-y-3 animate-in fade-in duration-150">
        <button
          type="button"
          onClick={() => setHomeFeature(null)}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[var(--brand-3)] hover:underline"
        >
          <ChevronLeft size={14} /> Back to Home
        </button>
        {homeFeature === 'summary' ? (
          <ContractSummary />
        ) : (
          <div className="ez-card overflow-hidden">
            {homeFeature === 'review' && <Playbook />}
            {homeFeature === 'risk' && <RiskAnalysis />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-in fade-in duration-150">
      {/* Risk Score gauge card */}
      <div className="ez-card p-4 flex items-center gap-4">
        <Gauge
          value={riskScore}
          loading={
            riskScore === null &&
            (autoStatus.playbook === 'running' ||
              autoStatus.risks === 'running' ||
              analyzing ||
              // Playbook is done but risks still running, or vice versa — keep
              // the spinner up so the score doesn't pop in partway through.
              (playbookSettled && !risksSettled) ||
              (risksSettled && !playbookSettled))
          }
        />
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--ink-4)]">
            Ezylex risk score
          </div>
          <p className="text-[12.5px] text-[var(--ink-2)] leading-relaxed mt-1.5">
            {riskScore === null ? (
              !bothSettled ? (
                <>
                  <strong>Analyzing this contract…</strong>{' '}
                  <span className="block mt-1.5 space-y-1">
                    <ProgressChip
                      label="Playbook"
                      state={
                        noPlaybook
                          ? 'none'
                          : autoStatus.playbook === 'running'
                          ? 'running'
                          : playbookSettled
                          ? 'done'
                          : 'queued'
                      }
                    />
                    <ProgressChip
                      label="AI Risks"
                      state={
                        autoStatus.risks === 'running'
                          ? 'running'
                          : risksSettled
                          ? 'done'
                          : 'queued'
                      }
                    />
                  </span>
                </>
              ) : (
                'Run a playbook or risk analysis to see this contract\'s score.'
              )
            ) : (
              <>
                <strong>
                  {noPlaybook
                    ? 'No playbook'
                    : `${stats.playbookGaps ?? 0} playbook issue${(stats.playbookGaps ?? 0) === 1 ? '' : 's'}`}
                </strong>
                {risks === null ? (
                  <> — tap Re-run to include AI risk analysis.</>
                ) : risks.length === 0 ? (
                  <> · no AI-identified risks.</>
                ) : (
                  <>
                    {' '}+{' '}
                    <strong>
                      {severityCounts.high} high
                      {severityCounts.medium > 0 ? `, ${severityCounts.medium} med` : ''}
                      {severityCounts.low > 0 ? `, ${severityCounts.low} low` : ''}
                    </strong>{' '}
                    AI risk{risks.length === 1 ? '' : 's'}.
                  </>
                )}
                {stats.openRedlines && stats.openRedlines > 0
                  ? ` ${stats.openRedlines} open redline${stats.openRedlines === 1 ? '' : 's'} to review.`
                  : ''}
              </>
            )}
          </p>
          <button
            type="button"
            disabled={analyzing}
            onClick={handleRerunAnalysis}
            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[var(--ink-2)] mt-2.5 px-2.5 py-1 rounded-md border border-[var(--line)] hover:bg-[var(--brand-tint)] hover:border-[var(--brand-3)] hover:text-[var(--brand-3)] disabled:opacity-60 disabled:cursor-wait"
          >
            <RefreshCw size={12} className={analyzing ? 'animate-spin' : ''} />
            {analyzing ? 'Analyzing…' : 'Re-run analysis'}
          </button>
        </div>
      </div>

      {/* Quick actions — each opens inline under Home with a back link. */}
      <div className="grid grid-cols-2 gap-2">
        <QuickAction
          icon={Sparkles}
          label="Ask AI"
          onClick={() => setHomeFeature('assistant')}
        />
        <QuickAction
          icon={ClipboardCheck}
          label="Redlines"
          onClick={() => setHomeFeature('review')}
          badge={stats.openRedlines || undefined}
        />
        <QuickAction
          icon={AlertTriangle}
          label="Risks"
          onClick={() => setHomeFeature('risk')}
          badge={risks && risks.length > 0 ? risks.length : undefined}
        />
        <QuickAction
          icon={FileText}
          label="Summary"
          onClick={() => setHomeFeature('summary')}
        />
      </div>


      {/* Key Terms */}
      {hasKeyTerms && (
        <div className="ez-card p-4">
          <h3 className="ez-sec-label mb-3">Key Terms</h3>
          <div className="space-y-2">
            {keyTerms!.contract_type_name && (
              <Term label="Type" value={keyTerms!.contract_type_name} />
            )}
            {keyTerms!.counter_party && (
              <Term label="Counterparty" value={keyTerms!.counter_party} />
            )}
            {keyTerms!.amount && (
              <Term
                label="Contract value"
                value={`$${keyTerms!.amount.toLocaleString()}`}
                emphasis
              />
            )}
            {keyTerms!.start_date && keyTerms!.end_date && (
              <Term
                label="Term"
                value={formatTermSpan(keyTerms!.start_date, keyTerms!.end_date)}
              />
            )}
            {keyTerms!.executed_date && (
              <Term label="Executed" value={formatDate(keyTerms!.executed_date)} />
            )}
            {keyTerms!.status && <Term label="Status" value={keyTerms!.status} />}
          </div>
        </div>
      )}

      <div className="h-2" />
    </div>
  );
}

function QuickAction({
  icon: Icon,
  label,
  onClick,
  badge,
}: {
  icon: typeof Sparkles;
  label: string;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex flex-col items-center justify-center gap-1.5 py-3 rounded-[var(--r-lg)] bg-white border border-[var(--line)] text-[12px] font-semibold text-[var(--ink-2)] transition-colors hover:bg-[var(--brand)] hover:text-white hover:border-[var(--brand)]"
    >
      <Icon size={19} className="text-[var(--brand-3)] group-hover:text-white" />
      {label}
      {!!badge && (
        <span
          className="absolute top-1.5 right-2 min-w-[16px] h-4 grid place-items-center rounded-md text-[10px] font-bold text-white px-1"
          style={{ background: 'var(--high)' }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function ProgressChip({
  label,
  state,
}: {
  label: string;
  state: 'running' | 'done' | 'queued' | 'none';
}) {
  const dotColor =
    state === 'done'
      ? 'var(--low)'
      : state === 'running'
      ? 'var(--brand-3)'
      : 'var(--ink-4)';
  const stateText =
    state === 'done'
      ? 'done'
      : state === 'running'
      ? 'scanning…'
      : state === 'none'
      ? 'no playbook'
      : 'queued';
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--ink-3)]">
      {state === 'running' ? (
        <Loader2 size={11} className="animate-spin" color="var(--brand-3)" />
      ) : state === 'done' || state === 'none' ? (
        <span
          className="inline-block w-2.5 h-2.5 rounded-full"
          style={{ background: dotColor }}
        />
      ) : (
        <span
          className="inline-block w-2.5 h-2.5 rounded-full border border-[var(--ink-4)]"
        />
      )}
      <span className="font-medium text-[var(--ink-2)]">{label}</span>
      <span>— {stateText}</span>
    </span>
  );
}

function Term({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[var(--line-2)] last:border-b-0">
      <span className="text-[12px] text-[var(--ink-3)]">{label}</span>
      <span
        className="text-[12.5px] font-semibold text-right ml-3 truncate max-w-[60%]"
        style={{ color: emphasis ? 'var(--brand-3)' : 'var(--ink)' }}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

function Gauge({ value, loading }: { value: number | null; loading?: boolean }) {
  const size = 92;
  const stroke = 9;
  const r = (size - stroke - 4) / 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const pct = value === null ? 0 : Math.min(100, Math.max(0, value));
  const color =
    value === null
      ? 'var(--ink-4)'
      : pct >= 60
      ? 'var(--high)'
      : pct >= 35
      ? 'var(--med)'
      : 'var(--low)';
  const label =
    value === null
      ? loading
        ? 'Analyzing'
        : '—'
      : pct >= 60
      ? 'High risk'
      : pct >= 35
      ? 'Moderate'
      : 'Low risk';

  // Spinner state: animated arc sweeping around the track while we wait
  // for the score to be computed.
  if (loading && value === null) {
    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="gauge-spin">
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke="var(--bg-soft-2)"
            strokeWidth={stroke}
          />
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke="var(--brand-3)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${circ * 0.25} ${circ * 0.75}`}
            transform={`rotate(-90 ${cx} ${cx})`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Loader2 className="animate-spin" size={20} color="var(--brand-3)" />
          <div className="text-[9px] font-semibold uppercase tracking-wider text-[var(--ink-3)] mt-1">
            Analyzing
          </div>
        </div>
        <style>{`
          @keyframes ezylex-gauge-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .gauge-spin { animation: ezylex-gauge-spin 1.2s linear infinite; transform-origin: 50% 50%; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke="var(--bg-soft-2)"
          strokeWidth={stroke}
        />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct / 100)}
          transform={`rotate(-90 ${cx} ${cx})`}
          style={{ transition: 'stroke-dashoffset 0.6s, stroke 0.3s' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[24px] font-bold leading-none" style={{ color }}>
          {value === null ? '—' : Math.round(pct)}
        </div>
        <div className="text-[9px] font-semibold uppercase tracking-wider text-[var(--ink-3)] mt-0.5">
          {label}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatTermSpan(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  try {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const months = Math.round(ms / (1000 * 60 * 60 * 24 * 30.44));
    if (months >= 12 && months % 12 === 0) {
      return `${months / 12} year${months === 12 ? '' : 's'}`;
    }
    return `${months} month${months === 1 ? '' : 's'}`;
  } catch {
    return '—';
  }
}

export default HomeScreen;
